import React, { useEffect, useMemo, useRef, useState } from "react";
import { Body5RegularTypography } from "../../../primitives/Typography/typographyVariants";
import { colorPalette } from "../../../theme/colors";
import { AudioRecorderWaveformProps } from "./AudioRecorderWaveform.types";
import { WaveformWrap } from "./AudioRecorderWaveform.styles";
import { FlexRow } from "../../../components/Layout/FlexRow/FlexRow";
import { FlexColumn } from "../../../components/Layout/FlexColumn/FlexColumn";
import Box from "../../../primitives/Box/Box";

const DEFAULT_WINDOW_SIZE = 70;

function clamp01(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

type VisibleBar = number | null;

export const AudioRecorderWaveform: React.FC<AudioRecorderWaveformProps> = ({
  levels,
  elapsedSec,
  windowSize = DEFAULT_WINDOW_SIZE,
  minBarPct = 20,
  maxBarPct = 100,
  disabled = false,
  dataCy = "audio-recorder-waveform",
}) => {
  const BAR_PX = 2;
  const GAP_PX = 2;
  const MIN_BARS = 8;

  const waveformRef = useRef<HTMLDivElement | null>(null);
  const [autoBarCount, setAutoBarCount] = useState<number | null>(null);

  const effectiveWindowSize = useMemo(() => {
    return Math.max(MIN_BARS, autoBarCount ?? windowSize);
  }, [autoBarCount, windowSize]);

  const visibleBars: VisibleBar[] = useMemo(() => {
    const safeLevels = (levels || []).map((v) => clamp01(v));

    // take last N samples for the window
    const start = Math.max(0, safeLevels.length - effectiveWindowSize);
    const window = safeLevels.slice(start);

    // If we have fewer than windowSize, RIGHT-pad with nulls so
    // the waveform grows left->right and remaining width stays empty.
    if (window.length < windowSize) {
      return window.concat(new Array(windowSize - window.length).fill(null));
    }

    return window;
  }, [levels, windowSize]);

  const barHeightsPct: Array<number | null> = useMemo(() => {
    const min = Math.max(0, Math.min(100, minBarPct));
    const max = Math.max(min, Math.min(100, maxBarPct));
    const range = max - min;

    return visibleBars.map((v) => {
      // empty slot -> no bar (keeps right side empty)
      if (v == null) return null;

      // keep some floor so real bars never disappear; scales 0..1 to [min..max]
      const h = min + v * range;
      return Math.max(1, Math.min(100, h));
    });
  }, [visibleBars, minBarPct, maxBarPct]);

  useEffect(() => {
    const el = waveformRef.current;
    if (!el) return;
    if (typeof ResizeObserver === "undefined") return;

    const compute = (width: number) => {
      const next = Math.max(
        MIN_BARS,
        Math.floor((width + GAP_PX) / (BAR_PX + GAP_PX))
      );
      setAutoBarCount((prev) => (prev === next ? prev : next));
    };

    compute(el.getBoundingClientRect().width);

    const ro = new ResizeObserver((entries) => {
      const w =
        entries[0]?.contentRect?.width ?? el.getBoundingClientRect().width;
      compute(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <FlexRow
      opacity={disabled ? 0.6 : 1}
      data-cy={dataCy}
      backgroundColor={colorPalette.popWhite[500]}
      outline={`1px solid ${colorPalette.popWhite[200]}`}
      borderRadius={8}
      px={8}
      py={4}
      gap={8}
      width="100%"
    >
      <WaveformWrap aria-label="recording-waveform" ref={waveformRef}>
        <FlexRow gap={2} width="100%" height="100%">
          {barHeightsPct.map((h, idx) => {
            const isEmpty = h == null;

            return (
              <FlexColumn
                key={idx}
                flex="0 0 2px"
                width="2px"
                minWidth="2px"
                height={isEmpty ? "0%" : h + "%"}
                backgroundColor={
                  isEmpty ? "transparent" : "background.neutral.secondary-hover"
                }
                borderRadius={isEmpty ? 0 : 3}
              />
            );
          })}
        </FlexRow>
      </WaveformWrap>

      <FlexRow
        flexShrink={0}
        justifyContent="flex-end"
        alignItems="center"
        gap={4}
        width="unset"
      >
        <Box
          height={8}
          width={8}
          borderRadius={999}
          backgroundColor="background.wishlink.default"
        />
        <Body5RegularTypography color={colorPalette.popBlack[500]} width={26}>
          {formatTime(elapsedSec)}
        </Body5RegularTypography>
      </FlexRow>
    </FlexRow>
  );
};
